import type { ConversationState, ConversationView, SendPolicy, UserView } from '@hilo/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { api, ApiError, isUnavailable } from '../../lib/api';
import { CHANNEL_LABEL, errorText, STATE_LABEL } from '../../lib/copy';
import {
  fetchAllMessages, fetchConnections, fetchConversation, fetchPolicy, fetchUsers, keys, pollInterval,
  refreshConversation, storeConversation,
} from '../../lib/queries';
import { useCurrentUser } from '../../lib/session';
import { formatRemaining } from '../../lib/time';
import { Avatar } from '../../ui/Avatar';
import { IconAlert, IconBack, IconCheck, IconClock, IconHand, IconPause, IconReopen } from '../../ui/icons';
import { Select } from '../../ui/Select';
import { useMediaQuery } from '../../ui/useMediaQuery';
import { useNow } from '../../ui/useNow';
import { type ActionFailure, actionNotice, type PaneAction } from './actionNotice';
import { ConversationThread } from './ConversationThread';
import { contactName, isUnnamed } from './names';
import { OriginPanel } from './OriginPanel';
import styles from './ConversationPane.module.css';
import { ReplyComposer } from './ReplyComposer';

const WINDOW_MS = 24 * 60 * 60 * 1000;

/** Acciones de estado permitidas (mismas transiciones que ConversationsService). */
const STATE_ACTIONS: Record<ConversationState, Array<{ to: ConversationState; label: string; icon: ReactNode }>> = {
  OPEN: [{ to: 'WAITING', label: 'Poner en espera', icon: <IconPause /> }, { to: 'RESOLVED', label: 'Resolver', icon: <IconCheck /> }],
  WAITING: [{ to: 'OPEN', label: 'Quitar de espera', icon: <IconReopen /> }, { to: 'RESOLVED', label: 'Resolver', icon: <IconCheck /> }],
  RESOLVED: [{ to: 'OPEN', label: 'Abrir de nuevo', icon: <IconReopen /> }],
};

/** Cada conversación monta su propio panel: el estado local (errores, borrador) no pasa de una a otra. */
export function ConversationRoute() {
  const { id = '' } = useParams();
  return <ConversationPane key={id} id={id} />;
}

function ConversationPane({ id }: { id: string }) {
  const client = useQueryClient();
  const user = useCurrentUser();
  const [searchParams] = useSearchParams();
  const [actionError, setActionError] = useState<ActionFailure | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const wide = useMediaQuery('(min-width: 75rem)');
  const phone = useMediaQuery('(max-width: 47.99rem)');
  const supervisor = user.role !== 'AGENT';

  const conversation = useQuery({ queryKey: keys.conversation(id), queryFn: () => fetchConversation(id), refetchInterval: pollInterval });
  const messages = useQuery({ queryKey: keys.messages(id), queryFn: ({ signal }) => fetchAllMessages(id, signal), refetchInterval: pollInterval });
  const policy = useQuery({ queryKey: keys.policy(id), queryFn: () => fetchPolicy(id), refetchInterval: pollInterval });
  const connections = useQuery({ queryKey: keys.connections, queryFn: fetchConnections, staleTime: 60_000 });
  const users = useQuery({ queryKey: keys.users, queryFn: fetchUsers, enabled: supervisor, staleTime: 60_000 });

  // Tras cualquier fallo el servidor tiene la verdad: se recarga y el aviso se decide con la vista nueva
  // (actionNotice). Si Hilo no respondía, la acción pudo aplicarse: no se da por fallida sin comprobarlo.
  const failed = (action: PaneAction, before: ActionFailure['before']) => (error: unknown) => {
    setActionError({ action, before, unanswered: isUnavailable(error), text: errorText(error), at: Date.now() });
    void refreshConversation(client, id);
  };
  const onActionSuccess = (view: ConversationView) => {
    setActionError(null);
    storeConversation(client, view);
    void refreshConversation(client, id);
  };

  const claim = useMutation({
    mutationFn: (revision: number) => api<ConversationView>('POST', `/conversations/${id}/claim`, { body: { expectedRevision: revision } }),
    onSuccess: onActionSuccess,
  });
  const changeState = useMutation({
    mutationFn: (input: { state: ConversationState; revision: number }) =>
      api<ConversationView>('PATCH', `/conversations/${id}/state`, { body: { state: input.state, expectedRevision: input.revision } }),
    onSuccess: onActionSuccess,
  });
  const assign = useMutation({
    mutationFn: (input: { assigneeId: string | null; revision: number }) =>
      api<ConversationView>('PUT', `/conversations/${id}/assignment`, { body: { assigneeId: input.assigneeId, expectedRevision: input.revision } }),
    onSuccess: onActionSuccess,
  });

  /** Ejecuta una acción con la revisión que se ve ahora (también al reintentar). */
  const run = (action: PaneAction) => {
    const current = conversation.data;
    if (!current) return;
    const { revision } = current;
    const onError = failed(action, { state: current.state, assigneeId: current.assigneeId });
    setActionError(null);
    if (action.kind === 'claim') claim.mutate(revision, { onError });
    else if (action.kind === 'state') changeState.mutate({ state: action.to, revision }, { onError });
    else assign.mutate({ assigneeId: action.assigneeId, revision }, { onError });
  };

  const back = (
    <Link className={styles.back} to={{ pathname: '/', search: searchParams.toString() }} aria-label="Volver a la bandeja">
      <IconBack />
    </Link>
  );

  if (conversation.isError && conversation.error instanceof ApiError && conversation.error.status === 404) {
    return (
      <section className={styles.gone}>
        {back}
        <span className={styles.goneIcon} aria-hidden="true"><IconAlert /></span>
        <h2>Esta conversación ya no está disponible</h2>
        <p>Ya no tienes acceso a ella o no existe. Si crees que es un error, habla con supervisión.</p>
      </section>
    );
  }
  if (!conversation.data) {
    return (
      <section className={styles.gone}>
        {back}
        <p role={conversation.isError ? 'alert' : 'status'}>
          {conversation.isError ? 'No se pudo abrir la conversación. Se volverá a intentar en unos segundos.' : 'Abriendo conversación…'}
        </p>
      </section>
    );
  }

  const view = conversation.data;
  const name = contactName(view);
  const mine = view.assigneeId === user.id;
  const canChangeState = mine || supervisor;
  const busy = claim.isPending || changeState.isPending || assign.isPending;
  const connection = connections.data?.find((item) => item.id === view.connectionId);
  const thread = view.state === 'RESOLVED' ? 'none' : view.assigneeId === null ? 'free' : mine ? 'mine' : 'other';
  const notice = actionError && actionNotice(actionError, view, conversation.dataUpdatedAt > actionError.at, user.id);

  return (
    <div className={styles.pane}>
      <header className={styles.header}>
        {back}
        <Avatar photoUrl={view.contactPhotoUrl} name={view.contactDisplayName} seed={view.contactId} thread={thread} channel={view.channel} size={phone ? 'md' : 'lg'} />
        <div className={styles.heading}>
          <h2 className={styles.name} title={name} data-unnamed={isUnnamed(view) || undefined}>{name}</h2>
          {isUnnamed(view) && <p className={styles.nameHint}>Nombre de perfil no disponible</p>}
          {/* Dos líneas: quién la atiende y por dónde llegó; así no se parte con separadores sueltos. */}
          <p className={styles.sub}>
            <span className={styles.state} data-state={view.state}>{STATE_LABEL[view.state]}</span>
            <span className={styles.subText}>
              {view.assigneeId === null ? 'Sin responsable' : mine ? 'La atiendes tú' : `La atiende ${view.assigneeDisplayName ?? 'otra persona'}`}
            </span>
          </p>
          <p className={styles.where}>
            <span className={styles.subText}>{CHANNEL_LABEL[view.channel]} · {view.connectionName}</span>
            {connection?.mode === 'TEST' && <span className={styles.flag}>Conexión de pruebas</span>}
          </p>
        </div>
        <div className={styles.actions}>
          {view.assigneeId === null && view.state !== 'RESOLVED' && (
            <button type="button" className="btn btn-primary" disabled={busy} onClick={() => run({ kind: 'claim', label: 'tomar la conversación' })}>
              <IconHand />
              {claim.isPending ? 'Tomando…' : 'Tomar conversación'}
            </button>
          )}
          {canChangeState && STATE_ACTIONS[view.state].map((action) => (
            <button key={action.to} type="button" className={`btn ${styles.ghost}`} disabled={busy}
              onClick={() => run({ kind: 'state', to: action.to, label: action.label.toLocaleLowerCase('es') })}>
              {action.icon}
              {action.label}
            </button>
          ))}
        </div>
      </header>

      {actionError && notice && (
        <div className={styles.alert} role="alert">
          <IconAlert />
          <p>{notice.text}</p>
          {notice.retry && (
            <button type="button" className={`btn btn-quiet ${styles.alertAction}`} disabled={busy} onClick={() => run(actionError.action)}>
              Reintentar
            </button>
          )}
        </div>
      )}
      {view.identityStatus === 'PENDING_REVIEW' && (
        <p className={styles.warning} role="note">
          <IconAlert />
          Identidad pendiente de revisión: este contacto llegó con un número que antes era de otra persona. No se puede responder hasta que un administrador lo revise.
        </p>
      )}

      <div className={styles.body}>
        <div className={styles.main}>
          <ConversationThread conversation={view} messages={messages} canReuse={mine && view.state !== 'RESOLVED'} />
          <ReplyComposer conversation={view} policy={policy} mine={mine} supervisor={supervisor} />
        </div>
        <aside className={styles.aside} aria-label="Detalles de la conversación">
          {/* En pantallas anchas siempre abierto; en las estrechas, plegado sobre el hilo. */}
          <details className={styles.details} open={wide || detailsOpen}
            onToggle={(event) => { if (!wide) setDetailsOpen(event.currentTarget.open); }}>
            <summary className={styles.summary}>Detalles de la conversación</summary>
            <div className={styles.cards}>
              <section className={styles.card}>
                <h3 className={styles.cardTitle}>Contacto</h3>
                <dl className={styles.facts}>
                  <div><dt>Nombre</dt><dd>{isUnnamed(view) ? `${name} (sin nombre de perfil)` : name}</dd></div>
                  <div><dt>Canal</dt><dd>{CHANNEL_LABEL[view.channel]} · {view.connectionName}</dd></div>
                  <div>
                    <dt>Identidad</dt>
                    <dd data-tone={view.identityStatus === 'PENDING_REVIEW' ? 'warning' : 'ok'}>
                      {view.identityStatus === 'PENDING_REVIEW' ? 'Pendiente de revisión' : 'Verificada'}
                    </dd>
                  </div>
                </dl>
              </section>
              <WindowCard policy={policy.data} channel={view.channel} timeZone={user.timezone} />
              <section className={styles.card}>
                <OriginPanel messages={messages.data ?? []} />
              </section>
              {supervisor && view.state !== 'RESOLVED' && (
                <Reassign key={`${view.id}-${view.revision}`} view={view} users={users.data ?? []} busy={busy}
                  onAssign={(assigneeId) => run({ kind: 'assign', assigneeId, label: assigneeId === null ? 'liberar la conversación' : 'reasignar la conversación' })}
                  self={user.id} />
              )}
            </div>
          </details>
        </aside>
      </div>
    </div>
  );
}

/** Límite desde el último entrante; HUMAN_AGENT solo para atención manual aprobada. */
function WindowCard({ policy, channel, timeZone }: { policy: SendPolicy | undefined; channel: ConversationView['channel']; timeZone: string }) {
  const now = useNow();
  const humanAgent = channel !== 'WHATSAPP' && policy?.replyWindow === 'HUMAN_AGENT';
  const closedLabel = channel === 'WHATSAPP' ? 'Cerrada: solo plantillas aprobadas' : 'Cerrada: espera un nuevo mensaje del cliente';
  let label = 'Comprobando…';
  let tone: 'ok' | 'low' | 'closed' | 'unknown' = 'unknown';
  let fraction = 0;
  if (policy?.validUntil) {
    const remaining = new Date(policy.validUntil).getTime() - now;
    fraction = Math.max(0, Math.min(1, remaining / (humanAgent ? 7 * WINDOW_MS : WINDOW_MS)));
    if (channel === 'WHATSAPP' ? remaining <= 0 : remaining < 0) {
      label = closedLabel;
      tone = 'closed';
    } else {
      label = `Quedan ${formatRemaining(policy.validUntil, new Date(now))}`;
      tone = remaining < 3 * 60 * 60 * 1000 ? 'low' : 'ok';
    }
  } else if (policy) {
    label = policy.reason === 'WINDOW_CLOSED' ? closedLabel : 'Sin ventana abierta';
    tone = 'closed';
  }
  return (
    <section className={styles.card}>
      <h3 className={styles.cardTitle}><IconClock /> Ventana de respuesta</h3>
      <p className={styles.windowLabel} data-tone={tone}>{label}</p>
      <div className={styles.meter} data-tone={tone} aria-hidden="true"><span style={{ width: `${fraction * 100}%` }} /></div>
      <p className={styles.cardNote}>
        {channel === 'WHATSAPP'
          ? 'WhatsApp permite respuestas libres durante 24 horas desde el último mensaje del cliente.'
          : humanAgent
            ? '7 días con HUMAN_AGENT: solo atención manual a consultas sin resolver, sin promociones. El plazo cuenta desde el último mensaje del cliente.'
            : '24 horas desde el último mensaje del cliente.'}
      </p>
      {channel !== 'WHATSAPP' && policy?.validUntil && <p className={styles.cardNote}>Vence: {new Date(policy.validUntil).toLocaleString('es-SV', { timeZone })}</p>}
    </section>
  );
}

function Reassign({ view, users, busy, self, onAssign }: {
  view: ConversationView; users: UserView[]; busy: boolean; self: string; onAssign: (assigneeId: string | null) => void;
}) {
  const [choice, setChoice] = useState(view.assigneeId ?? '');
  const active = users.filter((item) => item.active).sort((a, b) => a.displayName.localeCompare(b.displayName, 'es'));
  const current = view.assigneeId ?? '';
  return (
    <section className={styles.card}>
      <h3 className={styles.cardTitle}>Responsable</h3>
      <Select label="Responsable" hideLabel value={choice} onChange={setChoice}
        options={[
          { value: '', label: 'Sin responsable' },
          ...active.map((item) => ({ value: item.id, label: item.id === self ? `${item.displayName} (tú)` : item.displayName })),
        ]} />
      <button type="button" className="btn btn-secondary" disabled={busy || choice === current}
        onClick={() => onAssign(choice === '' ? null : choice)}>
        {choice === '' ? 'Liberar conversación' : 'Reasignar'}
      </button>
      <p className={styles.cardNote}>Solo quien atiende puede responder. Si quieres contestar tú, asígnatela primero.</p>
    </section>
  );
}
